import { cloneDeep } from 'lodash';

import { createReducer } from '@topconagriculture/frontend-utils';

import {
  getBunkScoreGrades,
  getSettingsManage,
  getThirdParties,
  getWeighBridgeSystemsList,
  resetBunkScoreGrades,
  updateDLCommunicationStarted,
  updateSettingsManage,
  updateThirdParty,
} from './settings.actions';
import {
  BunkReaderAdjustmentType,
  DlExtFeedLineType,
  DlRadioRadioType,
  DlType,
  FeedingSendMode,
  MixerChangesCWSaveMode,
  RefusalSubtractModeDefault,
  ScaleWeightUnitType,
  SettingsManage,
} from './settings.types';
import { timeZone } from '../../../config/constants';

export type SettingsState = SettingsManage;

export const initialState: SettingsState = {
  driverChanges: {
    mixerChangesDefaultSaveCW: true,
    mixerChangesDefaultSaveDM: false,
    mixerChangesDefaultSaveHC: false,
    mixerChangesCWSaveMode: MixerChangesCWSaveMode.DAILY_REQUIREMENT,
  },
  loadOptimization: {
    minimumSplitInclude: 100,
    minimumSplitSave: 100,
    optimizeLoadsMode: 0,
    feedingSendMode: FeedingSendMode.LOADS_MODE,
  },
  otherSettings: {
    rationEntryMethod: 1,
    currency: '',
    scaleWeightUnit: ScaleWeightUnitType.KG,
    useZoneControl: true,
    premixAmountsByPercentage: false,
    refusalSubtractModeDefault: RefusalSubtractModeDefault.PREVIOUS_DELIVERY,
    useWeightMotionTolerance: false,
    timeShift: 0,
    minimumLoadWeight: 0,
    timeZone,
    useLockedFeedings: false,
  },
  agentSettings: {
    hmsHCSystem: '',
    hmsHCImport: true,
    hmsMPSystem: '',
    hmsMPImport: true,
    hmsECSystem: '',
    hmsECImport: true,
    hmsAutoRead: true,
    hmsAgentActive: false,
  },
  dataLinkSettings: {
    dlType: DlType.WIFI,
    dlExtFeedLineType: DlExtFeedLineType.LEGACY,
    dlAutoSend: true,
    communicationInterval: 0,
    communicationStartTime: '',
    communicationEndTime: '',
    dlExtFilePath: '',
    dlAutoReceiveMode: 0,
    dlRadioComPort: 0,
    dlRadioReserveChannel: 0,
    dlRadioRadioType: DlRadioRadioType.STANDARD_RADIO_TYPE,
    dlLineByLine: false,
    dlIgnoreUnfinishedLoads: false,
    dlSupportOldIndicators: false,
    useMixerRoles: false,
    dlRadioReadsPerSecond: 0,
    dlRadioDeletesPerSecond: 0,
    dlRadioWaitSeconds: 0,
    dlWifiConnectTimeout: 0,
    dlWifiRetryTime: 0,
  },
  inventorySettings: { automateShrTime: '12.00', automateShrMonthday: 1, automateShrWeekday: 1 },
  weighbridgeSettings: { weighbridgeAutoRead: true, weighbridgeSystem: '', weighBridgeSystemsList: [] },
  bunkReaderScoreSettings: {
    bunkScoreGrades: null,
    brAdjustmentType: BunkReaderAdjustmentType.DAILY_REQUIREMENT,
    brAutoProcess: true,
    brDefaultscore: 5,
    brExpireTimeInHours: 4,
  },
  externalFeedingSystemsSettings: {
    fsAdvancedSettings: true,
    fsMineralsInGrams: true,
    fsAutoReceive: true,
    fsAutoSend: true,
    fsInputFolder: '',
    fsInputFilename: '',
    fsOutputFolder: '',
    fsOutputFilename: '',
    fsSendStartTime: '01.00',
    communicationInterval: 5,
    fsUseAgent: true,
  },
  operatorManagementSettings: {
    driverUsePermissions: false,
    driverRequirePIN: false,
    driverAutoLockScreen: false,
    driverAutoLockScreenDelay: 0,
  },
  thirdParties: [],
  defaultDensity: 0,
  dlCommunicationStarted: false,
  unlimitedFeedings: false,
};

const settingsReducer = createReducer(initialState, {
  [getSettingsManage.done.type]: (state: SettingsState, action: ReturnType<typeof getSettingsManage.done>) => {
    return {
      ...state,
      ...action.payload.result,
      bunkReaderScoreSettings: {
        bunkScoreGrades: state.bunkReaderScoreSettings.bunkScoreGrades,
        ...action.payload.result.bunkReaderScoreSettings,
      },
    };
  },
  [updateSettingsManage.done.type]: (state: SettingsState, action) => {
    const updatedSettings = action.payload.result;
    const settings = cloneDeep(state);
    for (const key of Object.keys(state.driverChanges)) {
      if (updatedSettings[key] !== undefined) settings.driverChanges[key] = updatedSettings[key];
    }
    for (const key of Object.keys(state.loadOptimization)) {
      if (updatedSettings[key] !== undefined) settings.loadOptimization[key] = updatedSettings[key];
    }
    for (const key of Object.keys(state.otherSettings)) {
      if (updatedSettings[key] !== undefined) settings.otherSettings[key] = updatedSettings[key];
    }
    for (const key of Object.keys(state.agentSettings)) {
      if (updatedSettings[key] !== undefined) settings.agentSettings[key] = updatedSettings[key];
    }
    for (const key of Object.keys(state.dataLinkSettings)) {
      if (updatedSettings[key] !== undefined) settings.dataLinkSettings[key] = updatedSettings[key];
    }
    for (const key of Object.keys(state.weighbridgeSettings)) {
      if (updatedSettings[key] !== undefined) settings.weighbridgeSettings[key] = updatedSettings[key];
    }
    for (const key of Object.keys(state.externalFeedingSystemsSettings)) {
      if (updatedSettings[key] !== undefined) settings.externalFeedingSystemsSettings[key] = updatedSettings[key];
    }
    for (const key of Object.keys(state.operatorManagementSettings)) {
      if (updatedSettings[key] !== undefined) settings.operatorManagementSettings[key] = updatedSettings[key];
    }
    if (updatedSettings.dlCommunicationStarted !== undefined) {
      settings.dlCommunicationStarted = updatedSettings.dlCommunicationStarted;
    }
    return settings;
  },
  [updateDLCommunicationStarted.done.type]: (
    state: SettingsState,
    action: ReturnType<typeof updateDLCommunicationStarted.done>,
  ) => {
    return {
      ...state,
      dlCommunicationStarted: action.payload.result,
    };
  },
  [getWeighBridgeSystemsList.done.type]: (
    state: SettingsState,
    action: ReturnType<typeof getWeighBridgeSystemsList.done>,
  ) => ({
    ...state,
    weighbridgeSettings: {
      ...state.weighbridgeSettings,
      weighBridgeSystemsList: action.payload.result,
    },
  }),
  [getThirdParties.done.type]: (state: SettingsState, action: ReturnType<typeof getThirdParties.done>) => ({
    ...state,
    thirdParties: action.payload.result,
  }),
  [updateThirdParty.done.type]: (state: SettingsState, action: ReturnType<typeof updateThirdParty.done>) => {
    const updatedParty = action.payload.result;
    const { id } = updatedParty;

    const thirdParties = state.thirdParties;
    return {
      ...state,
      thirdParties: thirdParties.map((party) => (party.id === id ? { ...party, ...updatedParty } : party)),
    };
  },
  [getBunkScoreGrades.done.type]: (state: SettingsState, action: ReturnType<typeof getBunkScoreGrades.done>) => ({
    ...state,
    bunkReaderScoreSettings: { ...state.bunkReaderScoreSettings, bunkScoreGrades: action.payload.result },
  }),
  [resetBunkScoreGrades.type]: (state: SettingsState) => ({
    ...state,
    bunkReaderScoreSettings: { ...state.bunkReaderScoreSettings, bunkScoreGrades: null },
  }),
});

export default settingsReducer;
